import { useState, useEffect, useCallback } from "react";
import {
  Container,
  Grid,
  Paper,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Divider,
  Button,
  Tooltip,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";

import PolicyRoundedIcon from "@mui/icons-material/PolicyRounded";
import FilterAltOffRoundedIcon from "@mui/icons-material/FilterAltOffRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

import ActionHeaderCard from "../components/General/ActionHeaderCard";
import AppDialog from "../components/General/Dialog/AppDialog";
import DatagridCustom from "../components/General/DatagridCustom";
import {
  getAuditLogs,
  getAuditLog,
  getAuditModules,
  getAuditActions,
} from "../services/AuditLogService";
import { getUsers } from "../services/UserService";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../context/ToastContext";

// Color del chip por acción. Las etiquetas vienen del backend (action_label).
const ACTION_COLORS = {
  created:          "success",
  updated:          "primary",
  deleted:          "error",
  enabled:          "success",
  disabled:         "warning",
  login:            "secondary",
  logout:           "warning",
  login_failed:     "error",
  service_executed: "info",
  generated:        "info",
  recalculated:     "info",
  forced:           "warning",
  synced:           "info",
  setting_updated:  "primary",
};

// Fallback si el backend no envía la etiqueta: "TipoOperacion" → "Tipo Operacion"
function humanize(value) {
  if (!value) return "";
  return String(value)
    .replace(/[_-]+/g, " ")
    .replace(/([a-záéíóúñ])([A-ZÁÉÍÓÚÑ])/g, "$1 $2")
    .toLowerCase()
    .replace(/(^|\s)\S/g, (c) => c.toUpperCase())
    .trim();
}

function ActionChip({ action, label }) {
  return (
    <Chip
      label={label || humanize(action)}
      color={ACTION_COLORS[action] || "default"}
      size="small"
      sx={{ fontWeight: 700, fontSize: 11 }}
    />
  );
}

function JsonBlock({ label, value }) {
  if (!value) return null;
  return (
    <Box sx={{ mb: 2 }}>
      <Typography
        variant="caption"
        sx={{ fontWeight: 700, opacity: 0.6, mb: 0.5, display: "block" }}
      >
        {label}
      </Typography>
      <Box
        component="pre"
        sx={{
          background: "rgba(0,0,0,0.05)",
          borderRadius: 2,
          p: 1.5,
          fontSize: 12,
          overflowX: "auto",
          whiteSpace: "pre-wrap",
          wordBreak: "break-all",
          m: 0,
        }}
      >
        {JSON.stringify(value, null, 2)}
      </Box>
    </Box>
  );
}

function DetailField({ label, value }) {
  if (!value && value !== 0) return null;
  return (
    <Box sx={{ mb: 1.5 }}>
      <Typography
        variant="caption"
        sx={{ fontWeight: 700, opacity: 0.55, display: "block" }}
      >
        {label}
      </Typography>
      <Typography variant="body2">{String(value)}</Typography>
    </Box>
  );
}

function formatDateTime(raw) {
  if (!raw) return "";
  const d = new Date(raw);
  if (isNaN(d.getTime())) return raw;
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

const EMPTY_FILTERS = {
  user_id:   "",
  module:    "",
  action:    "",
  date_from: null,
  date_to:   null,
};

export default function AuditLogs() {
  const { showToast } = useToast();
  const { hasPermission } = useAuth();
  const canViewDetail = hasPermission("ver detalle auditoria");

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [users, setUsers] = useState([]);
  const [modules, setModules] = useState([]);
  const [actions, setActions] = useState([]);

  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 50 });
  const [filterModel, setFilterModel] = useState({ items: [], quickFilterValues: [] });

  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLog, setDetailLog] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Carga todos los usuarios del sistema, excluyendo el id=1
  useEffect(() => {
    getUsers()
      .then((r) => {
        const all = r.data?.data || [];
        setUsers(all.filter((u) => u.id !== 1));
      })
      .catch(() => {});

    getAuditModules()
      .then((r) => setModules(r.data?.data || []))
      .catch(() => {});

    getAuditActions()
      .then((r) => setActions(r.data?.data || []))
      .catch(() => {});
  }, []);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const search = filterModel.quickFilterValues?.join(" ") || "";
      const params = {
        page: paginationModel.page + 1,
        per_page: paginationModel.pageSize,
        ...(filters.user_id  && { user_id:   filters.user_id }),
        ...(filters.module   && { module:    filters.module }),
        ...(filters.action   && { action:    filters.action }),
        ...(filters.date_from && { date_from: dayjs(filters.date_from).format("YYYY-MM-DD") }),
        ...(filters.date_to   && { date_to:   dayjs(filters.date_to).format("YYYY-MM-DD") }),
        ...(search            && { search }),
      };

      const res = await getAuditLogs(params);
      setRows(res.data?.data || []);
      setTotal(res.data?.meta?.total || 0);
    } catch {
      showToast("Error al cargar registros de auditoría.", "error");
    } finally {
      setLoading(false);
    }
  }, [paginationModel, filterModel, filters, showToast]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const handleClearFilters = () => {
    setFilters(EMPTY_FILTERS);
    setFilterModel({ items: [], quickFilterValues: [] });
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const openDetail = async (row) => {
    setDetailLog(null);
    setDetailOpen(true);
    setDetailLoading(true);
    try {
      const res = await getAuditLog(row.id);
      setDetailLog(res.data?.data || row);
    } catch {
      setDetailLog(row);
    } finally {
      setDetailLoading(false);
    }
  };

  const hasActiveFilters =
    filters.user_id || filters.module || filters.action ||
    filters.date_from || filters.date_to;

  const columns = [
    {
      field: "created_at",
      headerName: "Fecha / Hora",
      flex: 1.2,
      valueGetter: (v) => formatDateTime(v),
    },
    {
      field: "user_name",
      headerName: "Usuario",
      flex: 1,
      valueGetter: (v) => v || "—",
    },
    {
      field: "module",
      headerName: "Módulo",
      flex: 1.1,
      valueGetter: (v, row) => row.module_label || humanize(v),
    },
    {
      field: "action",
      headerName: "Acción",
      flex: 0.9,
      valueGetter: (v, row) => row.action_label || humanize(v),
      renderCell: (params) => (
        <ActionChip action={params.row.action} label={params.row.action_label} />
      ),
    },
    {
      field: "record_id",
      headerName: "ID Registro",
      flex: 0.7,
      valueGetter: (v) => v ?? "—",
    },
    {
      field: "description",
      headerName: "Descripción",
      flex: 2,
      valueGetter: (v) => v || "—",
    },
    {
      field: "ip_address",
      headerName: "IP",
      flex: 0.8,
      valueGetter: (v) => v || "—",
    },
    ...(canViewDetail
      ? [
          {
            field: "_detail",
            headerName: "",
            flex: 0.4,
            sortable: false,
            filterable: false,
            renderCell: (params) => (
              <Tooltip title="Ver detalle">
                <IconButton
                  size="small"
                  onClick={() => openDetail(params.row)}
                  color="primary"
                >
                  <InfoOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            ),
          },
        ]
      : []),
  ];

  return (
    <Container maxWidth="xl" sx={{ mt: 4 }}>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12 }}>
          <ActionHeaderCard
            icon={<PolicyRoundedIcon />}
            title="Auditoría del Sistema"
            description="Registro de acciones realizadas por los usuarios."
          />
        </Grid>

        {/* Filtros */}
        <Grid size={{ xs: 12 }}>
          <Paper elevation={0} variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
              <Typography variant="body2" sx={{ fontWeight: 700, opacity: 0.7 }}>
                Filtros
              </Typography>
              {hasActiveFilters && (
                <Tooltip title="Limpiar filtros">
                  <IconButton size="small" onClick={handleClearFilters} color="error">
                    <FilterAltOffRoundedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>

            <Grid container spacing={1.5}>
              {/* Usuario */}
              <Grid size={{ xs: 12, sm: 6, md: 3, lg: 2 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Usuario</InputLabel>
                  <Select
                    value={filters.user_id}
                    label="Usuario"
                    onChange={(e) => handleFilterChange("user_id", e.target.value)}
                  >
                    <MenuItem value="">Todos los usuarios</MenuItem>
                    {users.map((u) => (
                      <MenuItem key={u.id} value={u.id}>
                        {u.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Módulo */}
              <Grid size={{ xs: 12, sm: 6, md: 3, lg: 2 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Módulo</InputLabel>
                  <Select
                    value={filters.module}
                    label="Módulo"
                    onChange={(e) => handleFilterChange("module", e.target.value)}
                  >
                    <MenuItem value="">Todos los módulos</MenuItem>
                    {modules.map((m) => (
                      <MenuItem key={m.value} value={m.value}>
                        {m.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Acción */}
              <Grid size={{ xs: 12, sm: 6, md: 3, lg: 2 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Acción</InputLabel>
                  <Select
                    value={filters.action}
                    label="Acción"
                    onChange={(e) => handleFilterChange("action", e.target.value)}
                  >
                    <MenuItem value="">Todas las acciones</MenuItem>
                    {actions.map((a) => (
                      <MenuItem key={a.value} value={a.value}>
                        {a.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Fecha desde */}
              <Grid size={{ xs: 12, sm: 6, md: 3, lg: 2 }}>
                <DatePicker
                  label="Desde"
                  value={filters.date_from}
                  onChange={(v) => handleFilterChange("date_from", v)}
                  slotProps={{ textField: { size: "small", fullWidth: true } }}
                  maxDate={filters.date_to || undefined}
                />
              </Grid>

              {/* Fecha hasta */}
              <Grid size={{ xs: 12, sm: 6, md: 3, lg: 2 }}>
                <DatePicker
                  label="Hasta"
                  value={filters.date_to}
                  onChange={(v) => handleFilterChange("date_to", v)}
                  slotProps={{ textField: { size: "small", fullWidth: true } }}
                  minDate={filters.date_from || undefined}
                />
              </Grid>

              {hasActiveFilters && (
                <Grid
                  size={{ xs: 12, sm: 6, md: 3, lg: 2 }}
                  sx={{ display: "flex", alignItems: "center" }}
                >
                  <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    onClick={handleClearFilters}
                    startIcon={<FilterAltOffRoundedIcon />}
                    sx={{ borderRadius: 2, fontWeight: 700 }}
                  >
                    Limpiar
                  </Button>
                </Grid>
              )}
            </Grid>
          </Paper>
        </Grid>

        {/* Tabla */}
        <Grid size={{ xs: 12 }}>
          <DatagridCustom
            rows={rows}
            columns={columns}
            title="Registros de Auditoría"
            description={`${total} eventos registrados`}
            paginationModel={paginationModel}
            onPaginationChange={(model) => setPaginationModel(model)}
            total={total}
            loading={loading}
            filterModel={filterModel}
            onFilterChange={(model) => {
              setFilterModel(model);
              setPaginationModel((prev) => ({ ...prev, page: 0 }));
            }}
            filterMode="server"
            isServerPagination
          />
        </Grid>
      </Grid>

      {/* Dialog detalle */}
      <AppDialog
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        title="Detalle del evento"
        icon={<PolicyRoundedIcon />}
        maxWidth="sm"
        actions={null}
      >
        {detailLoading ? (
          <Typography
            variant="body2"
            sx={{ opacity: 0.5, textAlign: "center", py: 4 }}
          >
            Cargando...
          </Typography>
        ) : detailLog ? (
          <>
            <Box sx={{ display: "flex", gap: 1, mb: 2, flexWrap: "wrap" }}>
              <ActionChip action={detailLog.action} label={detailLog.action_label} />
              {detailLog.module && (
                <Chip
                  label={detailLog.module_label || humanize(detailLog.module)}
                  size="small"
                  variant="outlined"
                  sx={{ fontWeight: 700 }}
                />
              )}
            </Box>

            <DetailField label="Fecha / Hora"    value={formatDateTime(detailLog.created_at)} />
            <DetailField label="Usuario"         value={detailLog.user_name} />
            <DetailField label="ID de usuario"   value={detailLog.user_id} />
            <DetailField label="Módulo"          value={detailLog.module_label || humanize(detailLog.module)} />
            <DetailField label="ID del registro" value={detailLog.record_id} />
            <DetailField label="Descripción"     value={detailLog.description} />
            <DetailField label="Sucursal ID"     value={detailLog.branch_id} />
            <DetailField label="IP"              value={detailLog.ip_address} />

            {(detailLog.old_values || detailLog.new_values) && (
              <>
                <Divider sx={{ my: 2 }} />
                <JsonBlock label="Valores anteriores" value={detailLog.old_values} />
                <JsonBlock label="Valores nuevos"     value={detailLog.new_values} />
              </>
            )}
          </>
        ) : (
          <Typography
            variant="body2"
            sx={{ opacity: 0.5, textAlign: "center", py: 4 }}
          >
            Sin datos.
          </Typography>
        )}
      </AppDialog>
    </Container>
  );
}
